(() => {
  const CACHE = new Map();

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const soften = (r, g, b, variant = 'default') => {
    const chroma = variant === 'extension' ? 0.25 : 0.42;
    const whiteMix = 1 - chroma;
    r = Math.round(r * chroma + 255 * whiteMix);
    g = Math.round(g * chroma + 255 * whiteMix);
    b = Math.round(b * chroma + 255 * whiteMix);
    const mean = (r + g + b) / 3;
    const blend = variant === 'extension' ? 0.9 : 0.82;
    r = Math.round(r * blend + mean * (1 - blend));
    g = Math.round(g * blend + mean * (1 - blend));
    b = Math.round(b * blend + mean * (1 - blend));
    return [clamp(r, 0, 255), clamp(g, 0, 255), clamp(b, 0, 255)];
  };

  const LIGHT_MAT_FALLBACK = [248, 248, 248];
  const EXTENSION_MAT_LIGHTEN = 0.28;

  const computeAverage = (img, extension = false) => {
    const key = `${img.currentSrc || img.src}|${extension ? 'ext' : 'std'}`;
    if (CACHE.has(key)) return CACHE.get(key);
    try {
      const size = 24;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;
      let r = 0, g = 0, b = 0, count = 0;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] < 20) continue;
        const pr = data[i], pg = data[i + 1], pb = data[i + 2];
        // Ignore near-white / near-black logo pixels so mats follow brand colour (e.g. Kleemt ring)
        if (pr > 240 && pg > 240 && pb > 240) continue;
        if (pr < 20 && pg < 20 && pb < 20) continue;
        r += pr;
        g += pg;
        b += pb;
        count++;
      }
      if (!count) return LIGHT_MAT_FALLBACK;
      const variant = extension ? 'extension' : 'default';
      const softened = soften(Math.round(r / count), Math.round(g / count), Math.round(b / count), variant);
      CACHE.set(key, softened);
      return softened;
    } catch {
      return LIGHT_MAT_FALLBACK;
    }
  };

  const lightenRgb = (rgb, amount = 0.14) =>
    rgb.map((c) => Math.round(c + (255 - c) * amount));

  const applyToCard = (targetEl, img, options = {}) => {
    const run = () => {
      try {
        let rgb = computeAverage(img, options.extension) || LIGHT_MAT_FALLBACK;
        if (options.extension) rgb = lightenRgb(rgb, EXTENSION_MAT_LIGHTEN);
        targetEl.style.backgroundColor = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
      } catch (_) {}
    };
    if (img.complete) run();
    else img.addEventListener('load', run, { once: true });
  };

  const processSlCard = (card) => {
    const img = card.querySelector('img.sl-card__img');
    if (img) applyToCard(card, img);
  };

  const processProductCard = (card) => {
    const mediaInner = card.querySelector('.product-card__media-inner');
    const img = mediaInner?.querySelector('img');
    if (mediaInner && img) applyToCard(mediaInner, img);
  };

  const processProductCardSimple = (card) => {
    const media = card.querySelector('.product-card-simple__media');
    const img = media?.querySelector('img');
    if (media && img) applyToCard(media, img);
  };

  const processCardComplementary = (card) => {
    const imageEl = card.querySelector('.card-complementary__image');
    const img = imageEl?.querySelector('img');
    if (imageEl && img) applyToCard(imageEl, img);
  };

  const processPluginCard = (card) => {
    const mediaInner = card.querySelector('.plugin-card__media-inner');
    const img = mediaInner?.querySelector('img');
    const extension = card.classList.contains('plugin-card--extension');
    if (mediaInner && img) applyToCard(mediaInner, img, { extension });
  };

  const processPluginProductPage = (el) => {
    const mediaInner = el.querySelector('.plugin-product-page__media-inner');
    const img = mediaInner?.querySelector('.plugin-product-page__image, img');
    if (mediaInner && img) applyToCard(mediaInner, img);
  };

  /** Article hero media — same mat + complemented tint as `.plugin-card__media-inner` */
  const processArticleHeaderMedia = (wrapper) => {
    const mediaInner = wrapper.querySelector('.article-template-header-media-inner');
    const img = mediaInner?.querySelector('img.article-template-header-image');
    const extension = !!wrapper.closest('.extension-article');
    if (mediaInner && img) applyToCard(mediaInner, img, { extension });
  };

  const process = (el) => {
    if (el.matches('[data-sl-card]')) processSlCard(el);
    else if (el.classList.contains('article-template-header-image-wrapper'))
      processArticleHeaderMedia(el);
    else if (el.classList.contains('product-card')) processProductCard(el);
    else if (el.classList.contains('product-card-simple')) processProductCardSimple(el);
    else if (el.classList.contains('card-complementary')) processCardComplementary(el);
    else if (el.classList.contains('plugin-card')) processPluginCard(el);
    else if (el.classList.contains('plugin-product-page')) processPluginProductPage(el);
    else if (el.classList.contains('plugin-product-page__outer')) processPluginProductPage(el);
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      process(entry.target);
      io.unobserve(entry.target);
    });
  }, { rootMargin: '200px 0px' });

  const cardSelectors =
    '[data-sl-card], .product-card, .product-card-simple, .card-complementary, .plugin-card, .plugin-product-page, .plugin-product-page__outer, .article-template-header-image-wrapper';

  const observeCards = (container) => {
    if (!container) return;
    const cards = container.querySelectorAll(cardSelectors);
    cards.forEach((el) => io.observe(el));
    if (container.matches && container.matches(cardSelectors)) {
      io.observe(container);
    }
  };

  document.querySelectorAll(cardSelectors).forEach((el) => io.observe(el));

  const attachMutationObserver = (root) => {
    if (!root) return;
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) observeCards(node);
        });
      });
    });
    mo.observe(root, { childList: true, subtree: true });
  };

  document
    .querySelectorAll(
      '.load-more-grid, #product-grid, .collection-product-list, .plugin-collection-grid, quick-view-modal'
    )
    .forEach(attachMutationObserver);

  /* Recently viewed, popular products, recommendations inject cards after load */
  document
    .querySelectorAll('.recently-viewed, .popular-products, product-recommendations')
    .forEach(attachMutationObserver);

  document.body.addEventListener('quick-view-opened', (event) => {
    const container = event.detail?.container;
    if (!container) return;
    container.querySelectorAll('.plugin-product-page__outer').forEach((el) => process(el));
  });
})();
